import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { IconSpinner } from '../../components';

import IconLookingGlass from './IconLookingGlass';
import * as geocoderMapbox from './GeocoderMapbox';
import * as geocoderGoogleMaps from './GeocoderGoogleMaps';

import css from './LocationAutocompleteInput.module.css';

const DEBOUNCE_WAIT_TIME = 300;
const DEBOUNCE_WAIT_TIME_FOR_SHORT_QUERIES = 1000;
const KEY_CODE_ARROW_UP = 38;
const KEY_CODE_ARROW_DOWN = 40;
const KEY_CODE_ENTER = 13;
const KEY_CODE_TAB = 9;
const KEY_CODE_ESC = 27;
const DIRECTION_UP = 'up';
const DIRECTION_DOWN = 'down';
const TOUCH_TAP_RADIUS = 5; // Movement within 5px from touch start is considered a tap

// Touch devices need to be able to distinguish touches for scrolling and touches to tap
const getTouchCoordinates = nativeEvent => {
  const touch = nativeEvent && nativeEvent.changedTouches ? nativeEvent.changedTouches[0] : null;
  return touch ? { x: touch.screenX, y: touch.screenY } : null;
};

// Get correct geocoding variant: geocoderGoogleMaps or geocoderMapbox
const getGeocoderVariant = mapProvider => {
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  return isGoogleMapsInUse ? geocoderGoogleMaps : geocoderMapbox;
};

// Renders the autocompletion prediction results in a list
const LocationPredictionsList = props => {
  const {
    id,
    rootClassName,
    className,
    useDarkText,
    children,
    predictions,
    currentLocationId,
    geocoder,
    isGoogleMapsInUse,
    highlightedIndex,
    onSelectStart,
    onSelectMove,
    onSelectEnd,
  } = props;
  if (predictions.length === 0) {
    return null;
  }

  const item = (prediction, index) => {
    const isHighlighted = index === highlightedIndex;
    const predictionId = geocoder.getPredictionId(prediction);

    return (
      <li
        className={classNames(
          isHighlighted ? css.highlighted : null,
          useDarkText ? css.listItemBlackText : css.listItemWhiteText
        )}
        key={predictionId}
        id={predictionId}
        role="option"
        onTouchStart={e => {
          e.preventDefault();
          onSelectStart(getTouchCoordinates(e.nativeEvent));
        }}
        onMouseDown={e => {
          e.preventDefault();
          onSelectStart();
        }}
        onTouchMove={e => {
          e.preventDefault();
          onSelectMove(getTouchCoordinates(e.nativeEvent));
        }}
        onTouchEnd={e => {
          e.preventDefault();
          onSelectEnd(prediction);
        }}
        onMouseUp={e => {
          e.preventDefault();
          onSelectEnd(prediction);
        }}
      >
        {predictionId === currentLocationId ? (
          <span className={css.currentLocation}>
            <span aria-hidden="true" style={{ marginRight: 6 }}>📍</span>
            <FormattedMessage id="LocationAutocompleteInput.currentLocation" />
          </span>
        ) : (
          geocoder.getPredictionAddress(prediction)
        )}
      </li>
    );
  };

  const predictionRootMapProviderClass = isGoogleMapsInUse
    ? css.predictionsRootGoogle
    : css.predictionsRootMapbox;
  const classes = classNames(
    rootClassName || css.predictionsRoot,
    predictionRootMapProviderClass,
    className
  );

  return (
    <div className={classes} id={id}>
      <ul className={css.predictions} role="listbox">
        {predictions.map(item)}
      </ul>
      {children}
    </div>
  );
};

// Get the current value with defaults from the given
// LocationAutocompleteInput props.
const currentValue = props => {
  const value = props.input.value || {};
  const { search = '', predictions = [], selectedPlace = null } = value;
  return { search, predictions, selectedPlace };
};

class LocationAutocompleteInputImplementation extends Component {
  constructor(props) {
    super(props);

    this._isMounted = false;

    this.state = {
      inputHasFocus: false,
      selectionInProgress: false,
      touchStartedFrom: null,
      highlightedIndex: -1, // -1 means no highlight
      fetchingPlaceDetails: false,
      fetchingPredictions: false,
      // The predictions dropdown is rendered via React portal at
      // document.body level (see render() for the why). predictionsRect
      // tracks the input's bounding rect so the portaled dropdown can
      // be absolutely positioned just below it. Recomputed on focus,
      // scroll, and resize while the dropdown is open.
      predictionsRect: null,
      // Surfaces geocoder failures to the user instead of swallowing
      // them in a silent console.error. Per project rule (CLAUDE.md
      // "Fail loud, never fake"), the user must see when address
      // autocomplete is unavailable so they can either wait, retry,
      // or proceed by typing the address as-is.
      geocoderError: null,
    };

    // Ref to the input element.
    this.input = null;
    this.shortQueryTimeout = null;

    this.getGeocoder = this.getGeocoder.bind(this);
    this.currentPredictions = this.currentPredictions.bind(this);
    this.changeHighlight = this.changeHighlight.bind(this);
    this.selectPrediction = this.selectPrediction.bind(this);
    this.selectItemIfNoneSelected = this.selectItemIfNoneSelected.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onChange = this.onChange.bind(this);
    this.handleOnBlur = this.handleOnBlur.bind(this);
    this.handlePredictionsSelectStart = this.handlePredictionsSelectStart.bind(this);
    this.handlePredictionsSelectMove = this.handlePredictionsSelectMove.bind(this);
    this.handlePredictionsSelectEnd = this.handlePredictionsSelectEnd.bind(this);
    this.finalizeSelection = this.finalizeSelection.bind(this);
    this.updatePredictionsRect = this.updatePredictionsRect.bind(this);
    this.handleClearInput = this.handleClearInput.bind(this);
    this.setInputRef = this.setInputRef.bind(this);

    // Debounce the method to avoid calling the API too many times
    // when the user is typing fast.
    this.predict = debounce(this.predict.bind(this), DEBOUNCE_WAIT_TIME, { leading: true });
  }

  // Unconditional callback ref for the underlying <input>.
  //
  // WHY THIS EXISTS — DO NOT REPLACE WITH A CONDITIONAL `refMaybe` LADDER:
  //   The portal dropdown (see render() near the bottom) only mounts when
  //   `predictionsRect` is non-null. `predictionsRect` is computed from
  //   `this.input.getBoundingClientRect()` in updatePredictionsRect(). If
  //   `this.input` is never assigned, the rect stays null, and the entire
  //   predictions dropdown silently never appears — the input still works,
  //   the geocoder still fires, predictions still come back over the wire,
  //   they just have nowhere to render.
  //
  //   PR #87 introduced the portal but kept the prior conditional pattern
  //   (`typeof inputRef === 'function' ? attach-and-forward : inputRef ? { ref: inputRef } : {}`).
  //   That pattern only set `this.input` when the consumer passed a
  //   function ref. Object refs (useRef) were forwarded but not captured;
  //   no inputRef at all meant nothing was captured. Six of seven call-
  //   sites silently broke (every <LocationAutocompleteInput> /
  //   <FieldLocationAutocompleteInput> consumer; only DeliveryRangeCheck
  //   was fine because it bypasses this component entirely). The
  //   regression was cosmetically invisible — no error, no warning, the
  //   field just stopped autocompleting. This is the fourth time the
  //   "address autofill broken" symptom has recurred for a structurally
  //   different reason; please don't make it five. The contract here is:
  //   this component OWNS `this.input`; the consumer's inputRef is purely
  //   a forward. Don't refactor this into a conditional ladder unless
  //   you've audited every caller AND verified the portal still mounts.
  setInputRef(node) {
    this.input = node;
    const { inputRef } = this.props;
    if (typeof inputRef === 'function') {
      inputRef(node);
    } else if (inputRef && typeof inputRef === 'object') {
      inputRef.current = node;
    }
  }

  componentDidMount() {
    this._isMounted = true;
    // Window-level listeners reposition the portaled dropdown when the
    // page scrolls or the viewport resizes (rotation, virtual keyboard
    // open/close on mobile, desktop window resize).
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', this.updatePredictionsRect, true);
      window.addEventListener('resize', this.updatePredictionsRect);
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.shortQueryTimeout);
    this._isMounted = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.updatePredictionsRect, true);
      window.removeEventListener('resize', this.updatePredictionsRect);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // When focus opens the dropdown, capture the input's rect so the
    // portal renders in the right place. Clear the rect on close so a
    // re-open recomputes (cheap defensive against stale layout).
    if (this.state.inputHasFocus && !prevState.inputHasFocus) {
      this.updatePredictionsRect();
    } else if (!this.state.inputHasFocus && prevState.inputHasFocus) {
      this.setState({ predictionsRect: null });
    }
  }

  updatePredictionsRect() {
    if (!this._isMounted || !this.input || !this.state.inputHasFocus) return;
    const rect = this.input.getBoundingClientRect();
    this.setState({
      predictionsRect: {
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      },
    });
  }

  handleClearInput() {
    const { input } = this.props;
    // Reset to the SearchData empty shape (matches how the form expects
    // an unset value) and refocus so users can immediately type again.
    input.onChange({ search: '', predictions: [], selectedPlace: null });
    if (this.input) {
      this.input.focus();
    }
  }

  getGeocoder() {
    const geocoderVariant = getGeocoderVariant(this.props.config.maps.mapProvider);
    const Geocoder = geocoderVariant.default;

    // Create the Geocoder as late as possible only when it is needed.
    if (!this._geocoder) {
      this._geocoder = new Geocoder();
    }
    return this._geocoder;
  }

  currentPredictions() {
    const { search, predictions: fetchedPredictions } = currentValue(this.props);
    const { useDefaultPredictions = true, config } = this.props;
    const hasFetchedPredictions = fetchedPredictions && fetchedPredictions.length > 0;
    const showDefaultPredictions = !search && !hasFetchedPredictions && useDefaultPredictions;
    const geocoderVariant = getGeocoderVariant(config.maps.mapProvider);

    // A list of default predictions that can be shown when the user
    // focuses on the autocomplete input without typing a search. This can
    // be used to reduce typing and Geocoding API calls for common
    // searches.
    const defaultPredictions = (config.maps.search.suggestCurrentLocation
      ? [{ id: geocoderVariant.CURRENT_LOCATION_ID, predictionPlace: {} }]
      : []
    ).concat(config.maps.search.defaults);

    return showDefaultPredictions ? defaultPredictions : fetchedPredictions;
  }

  // Interpret input key event
  onKeyDown(e) {
    if (e.keyCode === KEY_CODE_ARROW_UP) {
      // Prevent changing cursor position in input
      e.preventDefault();
      this.changeHighlight(DIRECTION_UP);
    } else if (e.keyCode === KEY_CODE_ARROW_DOWN) {
      // Prevent changing cursor position in input
      e.preventDefault();
      this.changeHighlight(DIRECTION_DOWN);
    } else if (e.keyCode === KEY_CODE_ENTER) {
      const { selectedPlace } = currentValue(this.props);

      if (!selectedPlace) {
        // Prevent form submit, try to select value instead.
        e.preventDefault();
        e.stopPropagation();
        this.selectItemIfNoneSelected();
        this.input?.blur();
      }
    } else if (e.keyCode === KEY_CODE_TAB) {
      if (!e.shiftKey) {
        this.selectItemIfNoneSelected();
      }
    } else if (e.keyCode === KEY_CODE_ESC && this.input) {
      this.input.blur();
    }
  }

  // Handle input text change, fetch predictions if the value isn't empty
  onChange(e) {
    const onChange = this.props.input.onChange;
    const predictions = this.currentPredictions();
    const newValue = e.target.value;

    // Clear the current values since the input content is changed
    onChange({
      search: newValue,
      predictions: newValue ? predictions : [],
      selectedPlace: null,
    });

    // Clear highlighted prediction since the input value changed and
    // results will change as well. Also clear any prior geocoder
    // error so the message doesn't linger on subsequent successful
    // keystrokes.
    this.setState({ highlightedIndex: -1, geocoderError: null });

    if (!newValue) {
      // No need to fetch predictions on empty input
      return;
    }

    if (newValue.length >= 3) {
      if (this.shortQueryTimeout) {
        window.clearTimeout(this.shortQueryTimeout);
      }
      this.predict(newValue);
    } else {
      this.shortQueryTimeout = window.setTimeout(() => {
        this.predict(newValue);
      }, DEBOUNCE_WAIT_TIME_FOR_SHORT_QUERIES);
    }
  }

  // Change the currently highlighted item by calculating the new
  // index from the current state and the given direction number
  // (DIRECTION_UP or DIRECTION_DOWN)
  changeHighlight(direction) {
    this.setState((prevState, props) => {
      const predictions = this.currentPredictions();
      const currentIndex = prevState.highlightedIndex;
      let index = currentIndex;

      if (direction === DIRECTION_UP) {
        // Keep the first position if already highlighted
        index = currentIndex === 0 ? 0 : currentIndex - 1;
      } else if (direction === DIRECTION_DOWN) {
        index = currentIndex + 1;
      }

      // Check that the index is within the bounds
      if (index < 0) {
        index = -1;
      } else if (index >= predictions.length) {
        index = predictions.length - 1;
      }

      return { highlightedIndex: index };
    });
  }

  // Select the prediction in the given item. This will fetch/read the
  // place details and set it as the selected place.
  selectPrediction(prediction) {
    const currentLocationBoundsDistance = this.props.config.maps?.search
      ?.currentLocationBoundsDistance;
    this.props.input.onChange({
      ...this.props.input,
      selectedPlace: null,
    });

    this.setState({ fetchingPlaceDetails: true, geocoderError: null });

    // Promise.resolve() wrap — getGeocoder().getPlaceDetails() can
    // throw synchronously (e.g. window.mapboxgl/sdk not yet loaded
    // returning undefined client), and a bare .catch wouldn't catch
    // a sync throw — it would propagate uncaught, leaving the user
    // with a frozen input and no error feedback. Wrapping converts
    // sync throws into rejected promises that the .catch handles.
    Promise.resolve()
      .then(() =>
        this.getGeocoder().getPlaceDetails(prediction, currentLocationBoundsDistance)
      )
      .then(place => {
        if (!this._isMounted) {
          // Ignore if component already unmounted
          return;
        }
        this.setState({ fetchingPlaceDetails: false });
        this.props.input.onChange({
          search: place.address,
          predictions: [],
          selectedPlace: place,
        });
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.error(e);
        this.setState({
          fetchingPlaceDetails: false,
          geocoderError:
            'Address service is unavailable right now. Type your address as-is and continue. You can refine it later.',
        });
        this.props.input.onChange({
          ...this.props.input.value,
          selectedPlace: null,
        });
      });
  }
  selectItemIfNoneSelected() {
    if (this.state.fetchingPredictions) {
      // No need to select anything since prediction fetch is still going on
      return;
    }

    const { search, selectedPlace } = currentValue(this.props);
    const predictions = this.currentPredictions();
    if (!selectedPlace) {
      if (predictions && predictions.length > 0) {
        const geocoderVariant = getGeocoderVariant(this.props.config.maps.mapProvider);
        if (
          this.state.highlightedIndex === -1 &&
          predictions.length === 1 &&
          predictions[0].id === geocoderVariant.CURRENT_LOCATION_ID
        ) {
          // If the only prediction is the current location, do not select it automatically.
          return;
        }
        const index = this.state.highlightedIndex !== -1 ? this.state.highlightedIndex : 0;
        this.selectPrediction(predictions[index]);
      } else {
        this.predict(search);
      }
    }
  }
  predict(search) {
    const config = this.props.config;
    const onChange = this.props.input.onChange;
    this.setState({ fetchingPredictions: true, geocoderError: null });

    // See selectPrediction — Promise.resolve() wrap converts sync
    // throws (libraries not loaded yet) into rejected promises so
    // the .catch handles them. Without this, sync throws propagate
    // uncaught and the user sees no error.
    return Promise.resolve()
      .then(() =>
        this.getGeocoder().getPlacePredictions(
          search,
          config.maps.search.countryLimit,
          config.localization.locale
        )
      )
      .then(results => {
        const { search: currentSearch } = currentValue(this.props);
        this.setState({ fetchingPredictions: false });

        // If the earlier predictions arrive when the user has already
        // changed the search term, ignore and wait until the latest
        // predictions arrive. Without this logic, results for earlier
        // requests would override whatever the user had typed since.
        //
        // This is essentially the same as switchLatest in RxJS or
        // takeLatest in Redux Saga, without canceling the earlier
        // requests.
        if (results.search === currentSearch) {
          onChange({
            search: results.search,
            predictions: results.predictions,
            selectedPlace: null,
          });
        }
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.error(e);
        // The geocoder script may still be loading on first paint
        // (see route's prioritizeMapLibraryLoading flag). Distinguish
        // that from a hard failure so the user gets actionable text
        // instead of staring at a dead input. Either way, surface it
        // — never silent.
        const isLoading =
          e?.message?.includes?.('Mapbox libraries are required') ||
          e?.message?.includes?.('not ready') ||
          (typeof window !== 'undefined' && !window?.mapboxgl?.accessToken);
        this.setState({
          fetchingPredictions: false,
          geocoderError: isLoading
            ? 'Address autocomplete is loading. Give it a moment, then re-type. Or proceed by typing your address as-is.'
            : 'Address service is unavailable right now. Type your address as-is and continue. You can refine it later.',
        });
        const value = currentValue(this.props);
        onChange({
          ...value,
          selectedPlace: null,
        });
      });
  }

  finalizeSelection() {
    this.setState({ inputHasFocus: false, highlightedIndex: -1 });
    this.props.input.onBlur(currentValue(this.props));
  }

  handleOnBlur() {
    if (this.props.closeOnBlur && !this.state.selectionInProgress) {
      this.finalizeSelection();
    }
  }

  handlePredictionsSelectStart(touchCoordinates) {
    this.setState({
      selectionInProgress: true,
      touchStartedFrom: touchCoordinates,
      isSwipe: false,
    });
  }

  handlePredictionsSelectMove(touchCoordinates) {
    this.setState(prevState => {
      const touchStartedFrom = prevState.touchStartedFrom;
      const isTouchAction = !!touchStartedFrom;
      const isSwipe = isTouchAction
        ? Math.abs(touchStartedFrom.y - touchCoordinates.y) > TOUCH_TAP_RADIUS
        : false;

      return { selectionInProgress: false, isSwipe };
    });
  }

  handlePredictionsSelectEnd(prediction) {
    let selectAndFinalize = false;
    this.setState(
      prevState => {
        if (!prevState.isSwipe) {
          selectAndFinalize = true;
        }
        return { selectionInProgress: false, touchStartedFrom: null, isSwipe: false };
      },
      () => {
        if (selectAndFinalize) {
          this.selectPrediction(prediction);
          this.finalizeSelection();
        }
      }
    );
  }

  render() {
    const {
      autoFocus,
      rootClassName,
      className,
      useDarkText,
      iconClassName,
      CustomIcon,
      inputClassName,
      predictionsClassName,
      predictionsAttributionClassName,
      validClassName,
      placeholder = '',
      input,
      meta,
      disabled,
      config,
      intl,
      id,
      submitButton: SubmitButton,
      ariaLabel,
    } = this.props;
    const { name, onFocus } = input;
    const { search } = currentValue(this.props);
    const { touched, valid } = meta || {};
    const isValid = valid && touched;
    const predictions = this.currentPredictions();

    const ariaLabelMaybe = ariaLabel ? { ['aria-label']: ariaLabel } : {};

    const handleOnFocus = e => {
      // INTENTIONAL DUPLICATE PATH — handles the case where the
      // unconditional ref hasn't yet captured by the time the input is
      // focused. The primary path (componentDidUpdate -> updatePredictionsRect)
      // reads `this.input.getBoundingClientRect()`, but if the ref-attach
      // ordering ever drifts (HMR, autoFocus on first mount, future React
      // scheduling changes) we'd be back to a null rect and a missing
      // dropdown. Computing the rect directly from `e.target` here covers
      // that gap. Looks like a duplicate of updatePredictionsRect — it is,
      // on purpose. Don't remove it just to "deduplicate"; this is the
      // belt-and-suspenders that prevents the next "address autofill
      // broken" recurrence (see setInputRef comment for prior history).
      if (this._isMounted && e.target) {
        const rect = e.target.getBoundingClientRect();
        this.setState({
          inputHasFocus: true,
          predictionsRect: { top: rect.bottom, left: rect.left, width: rect.width },
        });
      } else {
        this.setState({ inputHasFocus: true });
      }
      onFocus(e);
    };

    const rootClass = classNames(rootClassName || css.root, className);
    const iconClass = classNames(iconClassName || css.icon);
    const inputClass = classNames(inputClassName || css.input, { [validClassName]: isValid });
    const predictionsClass = classNames(predictionsClassName);

    // Only render predictions when the input has focus. For
    // development and easier workflow with the browser devtools, you
    // might want to hardcode this to `true`. Otherwise the dropdown
    // list will disappear.
    const renderPredictions = this.state.inputHasFocus;
    const geocoderVariant = getGeocoderVariant(config.maps.mapProvider);
    const GeocoderAttribution = geocoderVariant.GeocoderAttribution;

    const predictionsId = `${id}.predictions`;

    return (
      <div className={rootClass}>
        <div className={iconClass}>
          {this.state.fetchingPlaceDetails ? (
            <IconSpinner className={css.iconSpinner} />
          ) : CustomIcon ? (
            <CustomIcon />
          ) : SubmitButton ? (
            <SubmitButton />
          ) : (
            <IconLookingGlass
              ariaLabel={intl.formatMessage({
                id: 'LocationAutocompleteInput.screenreader.search',
              })}
            />
          )}
        </div>
        <input
          className={inputClass}
          type="search"
          autoComplete="off"
          autoFocus={autoFocus}
          placeholder={placeholder}
          name={name}
          id={id}
          value={search}
          disabled={disabled || this.state.fetchingPlaceDetails}
          onFocus={handleOnFocus}
          onBlur={this.handleOnBlur}
          onChange={this.onChange}
          onKeyDown={this.onKeyDown}
          ref={this.setInputRef}
          title={search}
          data-testid="location-search"
          {...ariaLabelMaybe}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={renderPredictions}
          aria-controls={predictionsId}
          aria-activedescendant={predictions[this.state.highlightedIndex]?.id}
        />
        {/* Clear-X — visible whenever there's text in the field, on
            desktop and mobile. Lets the user reset and re-enter an
            address without first clearing each character manually. */}
        {search ? (
          <button
            type="button"
            className={css.clearButton}
            onClick={this.handleClearInput}
            // onMouseDown.preventDefault keeps the input focused so the
            // input.onBlur() flow doesn't fire and immediately close the
            // dropdown before our refocus runs.
            onMouseDown={e => e.preventDefault()}
            aria-label={intl.formatMessage({
              id: 'LocationAutocompleteInput.clearAddress',
            })}
          >
            ×
          </button>
        ) : null}
        {/* Geocoder error surfacing — per project rule (CLAUDE.md
            "Fail loud, never fake"), Mapbox failures are visible to
            the user instead of being swallowed in console.error. The
            text gives them an actionable next step (proceed by
            typing as-is) so they aren't stuck. */}
        {this.state.geocoderError ? (
          <p className={css.geocoderError} role="status" aria-live="polite">
            {this.state.geocoderError}
          </p>
        ) : null}
        {/*
          PORTAL: the predictions dropdown is rendered at document.body
          rather than as a sibling of the input. WHY THIS EXISTS — DO
          NOT REMOVE THE PORTAL:
            Multiple ancestors of LocationAutocompleteInput establish
            CSS stacking contexts (any flex parent with `position:
            relative` plus a transformed/filtered child can do it).
            The dropdown's high z-index is meaningless once it's
            trapped inside a lower-context parent — it ends up clipped
            by sibling content or hidden behind shadow layers. The
            symptom looks like "autocomplete broken." This bug
            recurred multiple times — once via the hero search rewrite,
            once via the topbar redesign, once via a search-bar layout
            audit — each time someone re-introduced a stacking context
            in a parent. The portal moves the dropdown outside every
            ancestor stacking context permanently, so future CSS
            changes upstream can't reintroduce the bug. If you're
            tempted to refactor this back inline ("simpler"), please
            don't — read the recurrence history first.
        */}
        {renderPredictions && this._isMounted && this.state.predictionsRect
          ? ReactDOM.createPortal(
              <div
                className={css.predictionsPortalWrapper}
                style={{
                  position: 'fixed',
                  top: this.state.predictionsRect.top,
                  left: this.state.predictionsRect.left,
                  width: this.state.predictionsRect.width,
                  // Above any ancestor stacking layer; the portal's
                  // body-level position means this is the simple stack
                  // depth, no parent context interference.
                  zIndex: 1000,
                }}
              >
                <LocationPredictionsList
                  id={predictionsId}
                  rootClassName={classNames(predictionsClass, css.predictionsPortalList)}
                  useDarkText={useDarkText}
                  predictions={predictions}
                  currentLocationId={geocoderVariant.CURRENT_LOCATION_ID}
                  isGoogleMapsInUse={config.maps.mapProvider === 'googleMaps'}
                  geocoder={this.getGeocoder()}
                  highlightedIndex={this.state.highlightedIndex}
                  onSelectStart={this.handlePredictionsSelectStart}
                  onSelectMove={this.handlePredictionsSelectMove}
                  onSelectEnd={this.handlePredictionsSelectEnd}
                >
                  <GeocoderAttribution
                    className={predictionsAttributionClassName}
                    useDarkText={useDarkText}
                  />
                </LocationPredictionsList>
              </div>,
              document.body
            )
          : null}
      </div>
    );
  }
}

/**
 * @typedef {Object} SearchData
 * @property {string} search
 * @property {Object} predictions
 * @property {Object} selectedPlace
 */

/**
 * @typedef {Object} SearchData
 * @property {Object} current
 */

/**
 * Location auto completion input component
 *
 * This component can work as the `component` prop to Final Form's
 * <Field /> component. It takes a custom input value shape, and
 * controls the onChange callback that is called with the input value.
 *
 * The component works by listening to the underlying input component
 * and calling a Geocoder implementation for predictions. When the
 * predictions arrive, those are passed to Final Form in the onChange
 * callback.
 *
 * See the LocationAutocompleteInput.example.js file for a usage
 * example within a form.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.iconClassName
 * @param {string?} props.inputClassName
 * @param {string?} props.predictionsClassName
 * @param {string?} props.predictionsAttributionClassName
 * @param {string?} props.validClassName
 * @param {boolean} props.autoFocus
 * @param {boolean} props.closeOnBlur
 * @param {string?} props.placeholder
 * @param {boolean} props.useDefaultPredictions
 * @param {Object} props.input
 * @param {string} props.input.name
 * @param {string|SearchData} props.input.value
 * @param {Function} props.input.onChange
 * @param {Function} props.input.onFocus
 * @param {Function} props.input.onBlur
 * @param {Object} props.meta
 * @param {boolean} props.meta.valid
 * @param {boolean} props.meta.touched
 * @param {Function | RefHook} props.inputRef
 * @param {ReactNode} props.CustomIcon override the default icon
 * @returns {JSX.Element} LocationAutocompleteInputImpl component
 */
const LocationAutocompleteInputImpl = props => {
  const config = useConfiguration();
  const intl = useIntl();

  return <LocationAutocompleteInputImplementation config={config} intl={intl} {...props} />;
};

export default LocationAutocompleteInputImpl;
